import { useParams } from 'react-router-dom'
import { useMerchantUploadInfo, useMerchantUpload } from '../hooks/useDocuments'
import { FileUploadZone } from '../components/lead/FileUploadZone'
import { type DocumentCategory } from '../lib/api'
import { useState } from 'react'

function formatTimeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now()
  if (diff <= 0) return 'Expired'
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  if (hours > 24) return `${Math.floor(hours / 24)} days, ${hours % 24} hours remaining`
  if (hours > 0) return `${hours} hours, ${minutes} minutes remaining`
  return `${minutes} minutes remaining`
}

export function MerchantDocumentUploadPage() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, error } = useMerchantUploadInfo(token)
  const uploadMutation = useMerchantUpload()
  const [uploadingCategory, setUploadingCategory] = useState<string | null>(null)
  const [lastUploadedCategory, setLastUploadedCategory] = useState<string | null>(null)

  if (isLoading) {
    return (
      <PageShell>
        <div className="text-center py-12 text-gray-500">Loading...</div>
      </PageShell>
    )
  }

  if (error || !data) {
    return (
      <PageShell>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <svg className="h-8 w-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Link Expired or Invalid</h1>
          <p className="text-sm text-gray-500">
            This upload link has expired or is no longer valid.<br />
            Please contact your AGMS representative for a new link.
          </p>
        </div>
      </PageShell>
    )
  }

  const allDone = data.remaining_categories.length === 0

  const handleUpload = (category: DocumentCategory, file: File) => {
    if (!token) return
    setUploadingCategory(category)
    setLastUploadedCategory(null)
    uploadMutation.mutate(
      { token, category, file },
      {
        onSuccess: () => {
          setLastUploadedCategory(category)
          setUploadingCategory(null)
        },
        onError: () => {
          setUploadingCategory(null)
        },
      },
    )
  }

  return (
    <PageShell>
      <div className="max-w-xl mx-auto">
        {/* Greeting */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">
            {data.merchant_name ? `Hi ${data.merchant_name},` : 'Document Upload'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            AGMS has requested the following documents to complete your application.
          </p>
          {data.message && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
              {data.message}
            </div>
          )}
        </div>

        {/* Expiry countdown */}
        <div className="mb-6 flex items-center gap-2 text-sm text-gray-500">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {formatTimeRemaining(data.expires_at)}
        </div>

        {allDone ? (
          <div className="text-center py-8 bg-green-50 rounded-lg border border-green-200">
            <svg className="mx-auto h-12 w-12 text-green-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-green-800">All documents uploaded!</h2>
            <p className="mt-1 text-sm text-green-600">
              Thank you. Your AGMS representative has been notified.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.categories.map((category) => {
              const isUploaded = data.uploaded_categories.includes(category)
              const rules = data.category_rules[category]
              const isUploading = uploadingCategory === category
              const justUploaded = lastUploadedCategory === category

              return (
                <div
                  key={category}
                  className="p-4 rounded-lg border border-gray-200 bg-white"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-gray-700">
                      {rules?.label ?? category}
                    </h2>
                    {isUploaded && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Uploaded
                      </span>
                    )}
                  </div>

                  {isUploaded ? (
                    <p className="text-xs text-gray-400">
                      You can upload again to replace this document.
                    </p>
                  ) : null}

                  {justUploaded && (
                    <div className="mb-2 p-2 rounded bg-green-50 text-xs text-green-700">
                      Uploaded successfully!
                    </div>
                  )}

                  <FileUploadZone
                    onFileSelect={(file) => handleUpload(category, file)}
                    accept={rules?.allowed_types ?? 'application/pdf'}
                    maxSizeMb={rules?.max_size_mb ?? 25}
                    isUploading={isUploading}
                    compact
                  />

                  {uploadMutation.isError && uploadingCategory === null && lastUploadedCategory === null && (
                    <p className="mt-1 text-xs text-red-500">Upload failed. Please try again.</p>
                  )}

                  <p className="mt-1 text-xs text-gray-400">
                    Max {rules?.max_size_mb ?? 25}MB.{' '}
                    {rules?.allowed_types?.includes('image') ? 'PDF, JPG, PNG, or HEIC' : 'PDF only'}
                  </p>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </PageShell>
  )
}

/** Minimal branded shell for the public upload page (no CRM nav). */
function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-[#1e3a5f] py-4 px-6">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="text-white text-lg font-bold">AGMS</div>
          <span className="text-blue-200 text-sm">Merchant Document Upload</span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  )
}

export default MerchantDocumentUploadPage
